/**
 * Leaf helpers for per-canvas share SEO (path slugs, nest-open, crawl links).
 * Keep free of package imports so Functions can copy this file at build.
 */

export const SEO_HTML_IMAGE_CAP = 24;

export const RESERVED_SHARE_SITE_FILES = [
  'index.html',
  'freeze.json',
  'current.json',
  'seo-index.json',
] as const;

export type SeoShareItem = {
  uuid: string;
  parentUuid: string | null;
  title: string;
  description?: string;
  imageIds?: string[];
  thumbId?: string;
  selectedTapAction?: 'auto' | 'view' | 'open' | string;
  linkOut?: string | null;
  /** Interior SVG canvas graphics (host `svgGraphics`). */
  svgGraphicsMediaId?: string;
};

export type SeoShareCanvas = {
  parentUuid: string | null;
  itemUuids: string[];
  /** SVG canvas background media id (from parent item svgGraphics). */
  svgBackgroundMediaId?: string;
};

export type SeoShareManifest = {
  shareId: string;
  slug: string | null;
  token: string | null;
  visibility: 'public' | 'private';
  version: number;
  title: string;
  description?: string;
  rootItemUuid: string;
  items: SeoShareItem[];
  canvases?: SeoShareCanvas[];
};

export type SeoCanvasListItem = {
  item: SeoShareItem;
  title: string;
  nestHref: string | null;
  linkOutHref: string | null;
  imageUrls: string[];
};

export type ShareSeoIndexImage = {
  loc: string;
  title: string;
};

export type ShareSeoIndexCanvas = {
  parentUuid: string;
  pathSegment: string | null;
  images: ShareSeoIndexImage[];
};

export type ShareSeoIndex = {
  version: 1;
  shareId: string;
  slug: string | null;
  canvases: ShareSeoIndexCanvas[];
};

/** ASCII kebab from an item title. Empty → `item`. */
export function itemPathSlug(title: string | null | undefined): string {
  const raw = String(title ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return raw || 'item';
}

/** `{slug}-{uuid}` — uuid is always the suffix (identity). */
export function canvasPathSegment(
  title: string | null | undefined,
  uuid: string,
): string {
  const id = String(uuid || '').trim();
  return `${itemPathSlug(title)}-${id}`;
}

export function isReservedShareSiteFile(name: string): boolean {
  const base = String(name || '')
    .replace(/\/+$/, '')
    .split('/')
    .pop() || '';
  return (RESERVED_SHARE_SITE_FILES as readonly string[]).includes(base);
}

/** Nested canvas dir under /s/{slug}/{segment}/ — not freeze.json etc. */
export function isCanvasPathSegment(name: string): boolean {
  const seg = String(name || '').replace(/\/+$/, '');
  if (!seg || seg.includes('/') || seg.includes('..')) return false;
  if (isReservedShareSiteFile(seg)) return false;
  if (/\.(json|html|txt|xml|js|css)$/i.test(seg)) return false;
  return seg.includes('-');
}

export function originFromCanonicalHost(host: string): string {
  const trimmed = String(host || '').replace(/\/$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^https?:\/\//i, '')}`;
}

export function shareRootPath(manifest: Pick<SeoShareManifest, 'visibility' | 'slug' | 'token' | 'shareId'>): string {
  if (manifest.visibility === 'public' && manifest.slug) {
    return `/s/${encodeURIComponent(manifest.slug)}/`;
  }
  const token = manifest.token || manifest.shareId || 'unknown';
  return `/share/${encodeURIComponent(token)}/`;
}

export function shareRootHref(
  host: string,
  manifest: Pick<SeoShareManifest, 'visibility' | 'slug' | 'token' | 'shareId'>,
): string {
  return `${originFromCanonicalHost(host)}${shareRootPath(manifest)}`;
}

export function shareFullMediaUrl(
  host: string,
  manifest: Pick<SeoShareManifest, 'shareId' | 'version'>,
  mediaId: string,
): string {
  return `${originFromCanonicalHost(host)}/m/${encodeURIComponent(manifest.shareId)}/v${manifest.version}/${encodeURIComponent(mediaId)}`;
}

export function resolveCanvasParentUuid(
  manifest: Pick<SeoShareManifest, 'rootItemUuid'>,
  requested?: string | null,
): string {
  const root = manifest.rootItemUuid;
  if (requested == null || requested === '' || requested === 'root' || requested === root) {
    return root;
  }
  return requested;
}

export function isShareRootCanvas(
  manifest: Pick<SeoShareManifest, 'rootItemUuid'>,
  canvasParentUuid: string | null | undefined,
): boolean {
  return resolveCanvasParentUuid(manifest, canvasParentUuid) === manifest.rootItemUuid;
}

export function itemsOnCanvas(
  manifest: SeoShareManifest,
  canvasParentUuid: string | null | undefined,
): SeoShareItem[] {
  const parent = resolveCanvasParentUuid(manifest, canvasParentUuid);
  const chunk = (manifest.canvases || []).find((c) => c.parentUuid === parent);
  if (chunk && chunk.itemUuids.length > 0) {
    const byUuid = new Map(manifest.items.map((i) => [i.uuid, i]));
    return chunk.itemUuids
      .map((id) => byUuid.get(id))
      .filter((i): i is SeoShareItem => Boolean(i));
  }
  return manifest.items.filter((i) => i.parentUuid === parent);
}

export function itemHasChildren(manifest: SeoShareManifest, itemUuid: string): boolean {
  if (manifest.items.some((i) => i.parentUuid === itemUuid)) return true;
  return (manifest.canvases || []).some(
    (c) => c.parentUuid === itemUuid && c.itemUuids.length > 0,
  );
}

export function itemHasCanvasBackground(
  manifest: SeoShareManifest,
  item: SeoShareItem,
): boolean {
  if (typeof item.svgGraphicsMediaId === 'string' && item.svgGraphicsMediaId.trim()) {
    return true;
  }
  return (manifest.canvases || []).some((c) => {
    if (c.parentUuid !== item.uuid) return false;
    return Boolean(c.svgBackgroundMediaId && c.svgBackgroundMediaId.trim());
  });
}

/**
 * Same nest-open rule as viewing-mode `auto`: open if children or canvas SVG bg.
 * Frozen `selectedTapAction` wins when set.
 */
export function isNestOpen(manifest: SeoShareManifest, item: SeoShareItem): boolean {
  const action = item.selectedTapAction || 'auto';
  if (action === 'view') return false;
  if (action === 'open') return true;
  return itemHasChildren(manifest, item.uuid) || itemHasCanvasBackground(manifest, item);
}

/** Canvas interiors that get their own nested HTML (not the list root, not outer null). */
export function nestableCanvasParentUuids(manifest: SeoShareManifest): string[] {
  const root = manifest.rootItemUuid;
  const fromChunks = (manifest.canvases || [])
    .map((c) => c.parentUuid)
    .filter((p): p is string => Boolean(p) && p !== root);
  const fromNest = manifest.items
    .filter((i) => i.uuid !== root && isNestOpen(manifest, i))
    .map((i) => i.uuid);
  return [...new Set([...fromChunks, ...fromNest])];
}

export function findItemByUuid(
  manifest: SeoShareManifest,
  uuid: string | null | undefined,
): SeoShareItem | undefined {
  if (!uuid) return undefined;
  return manifest.items.find((i) => i.uuid === uuid);
}

export function itemByCanvasPathSegment(
  manifest: SeoShareManifest,
  segment: string,
): SeoShareItem | undefined {
  const seg = String(segment || '').replace(/\/+$/, '');
  if (!seg) return undefined;
  const exact = manifest.items.find(
    (i) => canvasPathSegment(i.title, i.uuid) === seg,
  );
  if (exact) return exact;
  return manifest.items.find((i) => seg === `${itemPathSlug(i.title)}-${i.uuid}` || seg.endsWith(`-${i.uuid}`));
}

export function shareCanvasPath(
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): string {
  const parent = resolveCanvasParentUuid(manifest, canvasParentUuid);
  const rootPath = shareRootPath(manifest);
  if (parent === manifest.rootItemUuid) return rootPath;
  const item = findItemByUuid(manifest, parent);
  if (!item) return rootPath;
  return `${rootPath}${encodeURIComponent(canvasPathSegment(item.title, item.uuid))}/`;
}

export function shareCanvasHref(
  host: string,
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): string {
  return `${originFromCanonicalHost(host)}${shareCanvasPath(manifest, canvasParentUuid)}`;
}

/** Path for an item: nested canvas if it opens, else the canvas that contains it. */
export function shareItemPath(
  manifest: SeoShareManifest,
  item: SeoShareItem,
): string {
  if (item.uuid === manifest.rootItemUuid) {
    return shareCanvasPath(manifest, manifest.rootItemUuid);
  }
  if (isNestOpen(manifest, item)) {
    return shareCanvasPath(manifest, item.uuid);
  }
  const parent = item.parentUuid || manifest.rootItemUuid;
  return shareCanvasPath(manifest, parent);
}

/** Public URL for an item: nested canvas if it opens, else the canvas that contains it. */
export function shareItemHref(
  host: string,
  manifest: SeoShareManifest,
  item: SeoShareItem,
): string {
  return `${originFromCanonicalHost(host)}${shareItemPath(manifest, item)}`;
}

/** Path for an item, with `?item=` focus when the target is a leaf on a parent canvas. */
export function shareItemPathWithFocus(
  manifest: SeoShareManifest,
  item: SeoShareItem,
): string {
  const path = shareItemPath(manifest, item);
  if (item.uuid === manifest.rootItemUuid) return path;
  if (isNestOpen(manifest, item)) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}item=${encodeURIComponent(item.uuid)}`;
}

function parseLinkTarget(raw: string | null | undefined): { kind: 'item'; ref: string } | { kind: 'external'; href: string } | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const itemMatch = /^item:(.+)$/i.exec(trimmed);
  if (itemMatch) {
    const ref = itemMatch[1]?.trim();
    if (!ref) return null;
    return { kind: 'item', ref };
  }
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  ) {
    return { kind: 'external', href: trimmed };
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return { kind: 'external', href: trimmed };
  }
  return null;
}

export function resolveLinkOutHref(
  host: string,
  manifest: SeoShareManifest,
  linkOut: string | null | undefined,
): string | null {
  const parsed = parseLinkTarget(linkOut);
  if (!parsed) return null;
  if (parsed.kind === 'external') return parsed.href;
  const target = findItemByUuid(manifest, parsed.ref);
  if (!target) return null;
  return shareItemHref(host, manifest, target);
}

export function fullImageIdsForItem(item: SeoShareItem): string[] {
  const ids = Array.isArray(item.imageIds) ? item.imageIds : [];
  return ids.filter((id) => {
    const s = String(id || '').trim();
    if (!s) return false;
    if (/_thumb$/i.test(s)) return false;
    return true;
  });
}

export function canvasPageTitle(
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): string {
  const parent = resolveCanvasParentUuid(manifest, canvasParentUuid);
  if (parent === manifest.rootItemUuid) return manifest.title;
  return findItemByUuid(manifest, parent)?.title?.trim() || manifest.title;
}

/**
 * Share / OG description for a canvas page.
 * Root → manifest description (or title). Nested → container item description, else page title.
 */
export function canvasPageDescription(
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): string {
  const pageTitle = canvasPageTitle(manifest, canvasParentUuid);
  if (isShareRootCanvas(manifest, canvasParentUuid)) {
    const rootDesc = String(manifest.description || '').trim();
    return rootDesc || pageTitle;
  }
  const parent = resolveCanvasParentUuid(manifest, canvasParentUuid);
  const itemDesc = findItemByUuid(manifest, parent)?.description?.trim();
  return itemDesc || pageTitle;
}

/**
 * Prefer container thumb / full image, then first child image on the canvas,
 * then root thumb / image (incl. publish `card`).
 */
export function canvasOgImageMediaId(
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): string | null {
  const parent = resolveCanvasParentUuid(manifest, canvasParentUuid);
  const container = findItemByUuid(manifest, parent);
  if (container?.thumbId?.trim()) return container.thumbId.trim();
  const containerFull = container ? fullImageIdsForItem(container) : [];
  if (containerFull[0]) return containerFull[0]!;
  for (const child of itemsOnCanvas(manifest, parent)) {
    const ids = fullImageIdsForItem(child);
    if (ids[0]) return ids[0]!;
    const thumb = child.thumbId?.trim();
    if (thumb && !/_thumb$/i.test(thumb)) return thumb;
  }
  const root = findItemByUuid(manifest, manifest.rootItemUuid);
  if (root?.thumbId?.trim()) return root.thumbId.trim();
  const rootFull = root ? fullImageIdsForItem(root) : [];
  return rootFull[0] ?? null;
}

export function buildCanvasListItems(
  host: string,
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
  imageCap = SEO_HTML_IMAGE_CAP,
): { items: SeoCanvasListItem[]; htmlImageCount: number } {
  const listed = itemsOnCanvas(manifest, canvasParentUuid);
  let remaining = imageCap;
  const items: SeoCanvasListItem[] = listed.map((item) => {
    const all = fullImageIdsForItem(item).map((id) =>
      shareFullMediaUrl(host, manifest, id),
    );
    const take = all.slice(0, Math.max(0, remaining));
    remaining -= take.length;
    return {
      item,
      title: item.title?.trim() || 'Untitled',
      nestHref: isNestOpen(manifest, item)
        ? shareCanvasHref(host, manifest, item.uuid)
        : null,
      linkOutHref: resolveLinkOutHref(host, manifest, item.linkOut),
      imageUrls: take,
    };
  });
  return { items, htmlImageCount: imageCap - remaining };
}

export function allCanvasImages(
  host: string,
  manifest: SeoShareManifest,
  canvasParentUuid?: string | null,
): ShareSeoIndexImage[] {
  const out: ShareSeoIndexImage[] = [];
  for (const item of itemsOnCanvas(manifest, canvasParentUuid)) {
    const title = item.title?.trim() || 'Untitled';
    for (const id of fullImageIdsForItem(item)) {
      out.push({ loc: shareFullMediaUrl(host, manifest, id), title });
    }
  }
  return out;
}

export function buildShareSeoIndex(
  host: string,
  manifest: SeoShareManifest,
): ShareSeoIndex {
  const canvases: ShareSeoIndexCanvas[] = [
    {
      parentUuid: manifest.rootItemUuid,
      pathSegment: null,
      images: allCanvasImages(host, manifest, manifest.rootItemUuid),
    },
  ];
  for (const parentUuid of nestableCanvasParentUuids(manifest)) {
    const item = findItemByUuid(manifest, parentUuid);
    canvases.push({
      parentUuid,
      pathSegment: canvasPathSegment(item?.title, parentUuid),
      images: allCanvasImages(host, manifest, parentUuid),
    });
  }
  return {
    version: 1,
    shareId: manifest.shareId,
    slug: manifest.slug,
    canvases,
  };
}

export function parseCanvasParentFromPathname(
  pathname: string,
  manifest: SeoShareManifest,
): string | null {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean);
  if (parts.length < 3) return null;
  const kind = parts[0];
  if (kind !== 's' && kind !== 'share') return null;
  const segment = parts[2];
  if (!segment || isReservedShareSiteFile(segment) || !isCanvasPathSegment(segment)) {
    return null;
  }
  const item = itemByCanvasPathSegment(manifest, decodeURIComponent(segment));
  return item?.uuid ?? null;
}

/** Map a public /s or /share request file to the GCS object (or null = 404). */
export function resolveShareSiteGcsObject(
  kind: 's' | 'share',
  key: string,
  fileName: string,
): { objectPath: string; fallbackType: string } | null {
  const rawName = String(fileName || '').replace(/\/+$/, '');
  const sitePrefix = `shares-site/${kind}/${key}`;
  if (!rawName || rawName === 'index.html') {
    return {
      objectPath: `${sitePrefix}/index.html`,
      fallbackType: 'text/html; charset=utf-8',
    };
  }
  if (rawName === 'freeze.json' || rawName === 'current.json') {
    return {
      objectPath: `${sitePrefix}/${rawName}`,
      fallbackType: 'application/json',
    };
  }
  const nestedMatch = /^([^/]+)(?:\/index\.html)?$/i.exec(rawName);
  const firstSeg = nestedMatch?.[1] || '';
  if (firstSeg && isCanvasPathSegment(firstSeg)) {
    return {
      objectPath: `${sitePrefix}/${firstSeg}/index.html`,
      fallbackType: 'text/html; charset=utf-8',
    };
  }
  return null;
}
